import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import NotificationBanner from "./NotificationBanner";
import logoIcon from "../assets/images/logo-icon.svg";
import { useAuth } from "../contexts/Auth";

const Header = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const { isAuthenticated, isTeacher, logout } = useAuth();

    // Load dismissed notifications from local storage on component mount
    useEffect(() => {
        const dismissedNotifications = localStorage.getItem("dismissedNotifications");

        const initialNotifications = [
            // {
            //     id: "accountUpdateMessage20260613",
            //     message: t("notifications.accountUpdateMessage"),
            //     type: "success",
            // },
            // { id: "idYYYYMMDD", message: t("welcomeMessage"), type: "info" },
            /* {
                id: "aussprachetrainerv2_20251004",
                message: (
                    <span>
                        {t("notifications.aussprachetrainerv2_20251004_1")}
                        <a
                            href="https://docs.google.com/forms/d/e/1FAIpQLSdHAYWB8eHa-AdwHn5t5xXVJcYXjM95vCXqd12aoGn9sG8dKg/viewform"
                            className="underline font-semibold hover:opacity-80"
                            target="_blank"
                            rel="noreferrer"
                        >
                            {t("notifications.aussprachetrainerv2_20251004_2")}
                        </a>
                        {t("notifications.aussprachetrainerv2_20251004_3")}
                    </span>
                ),
                type: "success",
            }, */
        ];

        if (dismissedNotifications) {
            const dismissed = JSON.parse(dismissedNotifications);
            // Filter out notifications that have been dismissed
            setNotifications(
                initialNotifications.filter((notification) => !dismissed.includes(notification.id)),
            );
        } else {
            setNotifications(initialNotifications);
        }
    }, [t]);

    const handleLanguageChange = (e) => {
        i18n.changeLanguage(e.target.value);
    };

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const closeNotification = (id) => {
        // Remove the notification from the current state
        setNotifications(notifications.filter((notification) => notification.id !== id));

        // Store the dismissed notification ID in local storage
        const dismissedNotifications = localStorage.getItem("dismissedNotifications");
        let dismissed = dismissedNotifications ? JSON.parse(dismissedNotifications) : [];
        dismissed.push(id);
        localStorage.setItem("dismissedNotifications", JSON.stringify(dismissed));
    };

    const handleLogin = () => {
        navigate("/login");
    };

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    const handleRegister = () => {
        navigate("/register", {
            state: { signup_source: "header-signup-button" },
        });
    };

    const handleDashboard = () => {
        navigate("/dashboard");
    };

    return (
        <>
            <header className="relative flex items-center justify-between px-4 py-4 bg-white text-black shadow">
                <div className="flex items-center gap-0.5">
                    <a href="/" className="flex items-center gap-0.5">
                        <img src={logoIcon} alt="AusspracheTrainer Logo" className="h-12 w-12" />
                        <div className="text-xl font-medium tracking-normal">{t("appName")}</div>
                    </a>
                </div>

                {/* Desktop Menu */}
                <div className="hidden md:flex items-center gap-5">
                    {/* TEMPORARILY DISABLE ACCOUNTS
                    <button className="text-black py-1 hover-underline underline-black">
                        {t("blog")}
                    </button>
                    <button className="text-black py-1 hover-underline underline-black">
                        {t("instructions")}
                    </button>
                    */}
                    {!isAuthenticated && (
                        <>
                            <button
                                className="text-black py-1 hover-underline underline-black mr-[0.2rem]"
                                onClick={handleLogin}
                            >
                                {t("login")}
                            </button>
                            <button className="dopamine-button mr-1" onClick={handleRegister}>
                                {t("register")}
                            </button>
                        </>
                    )}

                    {isAuthenticated && (
                        <>
                            <button
                                className="text-black py-1 hover-underline underline-black mr-[0.2rem]"
                                onClick={handleLogout}
                            >
                                {t("logout")}
                            </button>
                            <button className="dopamine-button mr-1" onClick={handleDashboard}>
                                {t("dashboard")}
                            </button>
                        </>
                    )}

                    <div className="flex items-center gap-2">
                        <span
                            className={`cursor-pointer ${
                                i18n.language.startsWith("en") ? "font-bold" : ""
                            } hover-underline underline-black`}
                            onClick={() => handleLanguageChange({ target: { value: "en" } })}
                        >
                            EN
                        </span>
                        <span>|</span>
                        <span
                            className={`cursor-pointer ${
                                i18n.language.startsWith("de") ? "font-bold" : ""
                            } hover-underline underline-black`}
                            onClick={() => handleLanguageChange({ target: { value: "de" } })}
                        >
                            DE
                        </span>
                    </div>
                </div>

                {/* Hamburger Icon for Mobile */}
                <button
                    className="md:hidden flex flex-col justify-center items-center w-8 h-8 space-y-1.5"
                    onClick={toggleMenu}
                    aria-label="Toggle menu"
                >
                    <span
                        className={`block w-6 h-0.5 bg-black transform transition duration-300 ${
                            isMenuOpen ? "rotate-45 translate-y-2" : ""
                        }`}
                    ></span>
                    <span
                        className={`block w-6 h-0.5 bg-black transition duration-300 ${
                            isMenuOpen ? "opacity-0" : "opacity-100"
                        }`}
                    ></span>
                    <span
                        className={`block w-6 h-0.5 bg-black transform transition duration-300 ${
                            isMenuOpen ? "-rotate-45 -translate-y-2" : ""
                        }`}
                    ></span>
                </button>

                {/* Mobile Menu */}
                {isMenuOpen && (
                    <div className="absolute top-full left-0 right-0 bg-white shadow-lg z-50 md:hidden">
                        <div className="flex flex-col p-4 space-y-4">
                            {/*
                            <button className="text-black py-2 text-middle">{t("blog")}</button>
                            <button className="text-black py-2 text-middle">
                                {t("instructions")}
                            </button>
                            */}
                            {!isAuthenticated && (
                                <>
                                    <button
                                        className="text-black py-2 text-middle"
                                        onClick={handleLogin}
                                    >
                                        {t("login")}
                                    </button>
                                    <button
                                        className="dopamine-button w-full py-2 text-center"
                                        onClick={handleRegister}
                                    >
                                        {t("register")}
                                    </button>
                                </>
                            )}
                            {isAuthenticated && (
                                <>
                                    {isTeacher && (
                                        <button
                                            className="text-black py-2 text-middle"
                                            onClick={() => navigate("/teach")}
                                        >
                                            {t("teach.title")}
                                        </button>
                                    )}
                                    <button
                                        className="text-black py-2 text-middle"
                                        onClick={handleLogout}
                                    >
                                        {t("logout")}
                                    </button>
                                    <button
                                        className="dopamine-button w-full py-2 text-center"
                                        onClick={handleDashboard}
                                    >
                                        {t("dashboard")}
                                    </button>
                                </>
                            )}

                            <div className="flex items-center gap-2 py-2 justify-center">
                                <span
                                    className={`cursor-pointer ${
                                        i18n.language === "en" ? "font-bold" : ""
                                    } `}
                                    onClick={() =>
                                        handleLanguageChange({ target: { value: "en" } })
                                    }
                                >
                                    EN
                                </span>
                                <span>|</span>
                                <span
                                    className={`cursor-pointer ${
                                        i18n.language === "de" ? "font-bold" : ""
                                    }`}
                                    onClick={() =>
                                        handleLanguageChange({ target: { value: "de" } })
                                    }
                                >
                                    DE
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </header>
            {/* Render all active notifications */}
            <div className="notification-stack">
                {notifications.map((notification) => (
                    <div key={notification.id}>
                        <NotificationBanner
                            show={true}
                            onClose={() => closeNotification(notification.id)}
                            message={notification.message}
                            type={notification.type}
                        />
                    </div>
                ))}
            </div>
        </>
    );
};

export default Header;
